import Cache from '@fs/zion-cache'

// Generates a new object for the initialState to prevent unexpected stale references in nested objects.
export const initialState = {
  coupleRelationships: [],
  // this is for displaying an overlay for axios errors
  errorCode: null,
  errorFocusBackRef: null,
  errorType: null,
  // this is a callback to be invoked for certain errors on try again click
  handleErrorNavigation: null,
  // Whether the member's account is activated
  isAccountActivated: null,
  // this is to store membership info during the account process of transitioning from a public to a member account
  membershipInfo: {},
  memberUuid: '',
  metadata: {
    // This is the parent that connects the memberPerson to the workingPerson?. Needed for late-flow deductions.
    connectingParent: {}, // Useful for grandparents, siblings, and children logic with connecting parents
    connectedRelatives: {}, // This is a map of relatives that are connected to the user, used for knowing whether we should skip the connect-name step for selected matches.
    currentOrdinanceStatusReasons: '',
    gapParents: {}, // Keep track of whether user chose relation with gapParents and their choices for adding them
    lastCompletedStep: '',
    manuallyEnteredPerson: null, // When the user selects a match, we use this to store the manually entered person in case we need to restore it into the `persons` array.
    memberPerson: null,
    potentialMatches: [],
    relation: '',
    relationSpecific: '',
    // TODO: To truly keep track of whether an existing tree person was edited, we'll need to store an original object.
    // Currently, the user can edit any part of the tree person to toggle this to true, but if they edit it to the original value, it will still be set to `true` even though it should be `false`.
    treePersonWasEdited: false,
    wasLivingPerson: false,
  },
  parentChildRelationships: [],
  // NOTE: the backend will create every person that is in the `persons` array. With the exception of TreeList,
  //       this array should only contain the `workingPerson` and, when applicable, the gap parent.
  // NOTE: After the TreeList step, the workingPersons is guaranteed to be the first element, the only other element would be a gapParent (or gapSpouse).
  persons: [],
  portraits: {}, // Fetched portraits for relatives
  preparedPersons: null, // The user's list of reserved names for the temple.
  checkedStatusesCacheUpdated: false, // Flag to indicate when cache has been updated for NameCards
}

export const stepToPercentage = {
  undefined: 5,
  relation: 10,
  'tree-list': 15,
  name: 25,
  vitals: 35,
  confirm: 40,
  edit: 40,
  'select-parents': 45,
  father: 50,
  mother: 50,
  spouse: 50,
  match: 58,
  'death-before-eight': 66,
  stillborn: 66,
  'name-correction': 66,
  'vitals-correction': 66,
  'not-available': 66,
  reserve: {
    start: 58,
    'add-name': 66,
    'connect-name': 66,
    'edit-name': 66,
    'reserve-name': 75,
  },
  'ordinances-ready-reserve': {
    start: 50,
    'ordinances-ready-request-names': 66,
    'ordinances-ready-reserve-names': 75,
  },
  'ordinances-available-completed-reserved': 66,
  'names-ready': 80,
  print: 90,
  finish: 100,
}
export const addNameFlowSteps = Object.keys(stepToPercentage)

export const ordinancesReadySteps = ['ordinances-ready-reserve']

export const accountSetupFlowSteps = [
  '',
  'created',
  'add-credentials',
  'existing',
  'connect',
  'confirmed',
  'review',
  'sign-out',
  'create-new',
  'assistance',
  'already-assigned',
]

export function reducer(state, action) {
  switch (action.type) {
    case 'RESET_PERSON_DATA': {
      const newState = structuredClone(initialState)

      // Here are a few state items that should not be reset
      newState.isAccountActivated = state.isAccountActivated
      newState.membershipInfo = state.membershipInfo
      newState.memberUuid = state.memberUuid

      return newState
    }
    case 'SET_ERROR_TYPE':
      return {
        ...state,
        errorCode: action?.errorCode,
        errorFocusBackRef: action?.errorFocusBackRef,
        errorType: action.errorType,
        handleErrorNavigation: action?.handleErrorNavigation,
      }
    case 'SET_IS_ACCOUNT_ACTIVATED':
      return { ...state, isAccountActivated: action.isAccountActivated }
    case 'SET_WORKING_PERSON_CONTAINER': {
      return {
        ...state,
        metadata: { ...state.metadata, ...(action.metadata ?? {}) },
        persons: action.persons ?? state.persons,
        coupleRelationships: action.coupleRelationships ?? state.coupleRelationships,
        parentChildRelationships: action.parentChildRelationships ?? state.parentChildRelationships,
      }
    }
    case 'SET_MEMBERSHIP_INFO':
      return { ...state, membershipInfo: { ...state.membershipInfo, ...action.membershipInfo } }
    case 'SET_MEMBER_UUID':
      return { ...state, memberUuid: action.memberUuid }
    case 'SET_PORTRAITS':
      return { ...state, portraits: { ...state.portraits, ...action.portraits } }
    case 'SET_PREPARED_PERSONS':
      // eslint-disable-next-line no-case-declarations
      const sessionCache = new Cache({ storeName: 'ftte', type: 'session' })

      // Update sessionStorage so state is accessible between tabs (f.ex. when opening PDF to print name cards)
      sessionCache.getItem(`reservedNamesCheckedStatus:${action.memberUuid}`).then((checkedStatuses) => {
        let newCheckedStatuses = []
        // If checkedStatuses is falsy, create new object with all persons from `preparedPersons`
        if (!checkedStatuses) {
          newCheckedStatuses = action.preparedPersons.reduce((acc, person) => ({ ...acc, [person.id]: true }), {})
        } else {
          // identify new pids (in case new person was created and now needs to be added to the checked state)
          newCheckedStatuses = action.preparedPersons.reduce((acc, person) => {
            const newCheckedStatus = checkedStatuses[person.id]
            return { ...acc, [person.id]: newCheckedStatus === undefined ? true : newCheckedStatus }
          }, {})
        }
        sessionCache.setItem(`reservedNamesCheckedStatus:${action.memberUuid}`, newCheckedStatuses).then(() => {
          // Dispatch flag to indicate cache has been updated
          action.dispatch?.({
            type: 'SET_CHECKED_STATUSES_CACHE_UPDATED',
            checkedStatusesCacheUpdated: true,
          })
        })
      })

      // Update context state
      return { ...state, preparedPersons: action.preparedPersons, checkedStatusesCacheUpdated: false }
    case 'SET_CHECKED_STATUSES_CACHE_UPDATED':
      return { ...state, checkedStatusesCacheUpdated: action.checkedStatusesCacheUpdated }
    case 'SET_METADATA':
      return {
        ...state,
        metadata: {
          ...state.metadata,
          ...action.metadata,
        },
      }
    default:
      return state
  }
}
